import { useState } from 'react'
import { router, Link } from '@inertiajs/react'
import { useTranslation } from 'react-i18next'
import { AdminLayout } from '../../../layouts/admin-layout'
import { Badge } from '../../../components/ui/badge'
import { Input } from '../../../components/ui/input'
import { Button } from '../../../components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select'

interface Organization {
  id: number
  name: string
  description: string | null
  created_at: string
  updated_at: string
  users_count: number
  current_plan: string
  plan_display_name: string
  credits_purchased: number
  credits_used: number
  credits_available: number
  subscription_status: string
  monthly_revenue: number
  last_activity_at: string | null
  internal: boolean
}

interface Pagination {
  page: number
  count: number
  pages: number
  last: number
  from: number
  to: number
  prev: number | null
  next: number | null
}

interface Filters {
  subscription_status: string
  plan: string | null
  sort: string | null
}

interface AdminOrganizationsIndexProps {
  organizations: Organization[]
  pagy: Pagination
  filters: Filters
  auth: {
    user: {
      id: number
      name: string
      email: string
      admin: boolean
      super_admin?: boolean
    }
  }
  flash?: {
    success?: string
    error?: string
    notice?: string
  }
}

function Index({ organizations, pagy, filters }: AdminOrganizationsIndexProps) {
  const { t } = useTranslation('admin')
  const [searchName, setSearchName] = useState('')

  const buildParams = (overrides: Record<string, string | null> = {}) => {
    const params: Record<string, string> = {}

    // Preserve search filters
    if (searchName) params['q[name_cont]'] = searchName

    // Preserve current filters
    if (filters.subscription_status && filters.subscription_status !== 'active') {
      params['subscription_status'] = filters.subscription_status
    }
    if (filters.plan) params['plan'] = filters.plan
    if (filters.sort) params['sort'] = filters.sort

    // Apply overrides
    for (const [key, value] of Object.entries(overrides)) {
      if (value === null || value === '') {
        delete params[key]
      } else {
        params[key] = value
      }
    }

    return params
  }

  const handleSearch = () => {
    const params: Record<string, string> = {}

    if (searchName) params['q[name_cont]'] = searchName
    if (filters.subscription_status) params['subscription_status'] = filters.subscription_status
    if (filters.plan) params['plan'] = filters.plan
    if (filters.sort) params['sort'] = filters.sort

    router.get('/admin/organizations', params, { preserveState: true })
  }

  const handleReset = () => {
    setSearchName('')
    // Reset to defaults: active subscriptions, paid plans only
    router.get('/admin/organizations', { subscription_status: 'active', plan: 'paid' }, { preserveState: true })
  }

  const handleSubscriptionStatusChange = (value: string) => {
    router.get('/admin/organizations', buildParams({ subscription_status: value }), { preserveState: true })
  }

  const handlePlanChange = (value: string) => {
    router.get('/admin/organizations', buildParams({ plan: value === 'all' ? null : value }), { preserveState: true })
  }

  const handleSortChange = (value: string) => {
    router.get('/admin/organizations', buildParams({ sort: value === 'default' ? null : value }), { preserveState: true })
  }

  const handleSort = (field: string) => {
    const params = new URLSearchParams(window.location.search)
    const currentSort = params.get('q[s]')

    // Toggle between asc and desc
    let newSort = `${field} asc`
    if (currentSort === `${field} asc`) {
      newSort = `${field} desc`
    }

    const searchParams: Record<string, string> = {}

    // Preserve existing search filters
    if (searchName) searchParams['q[name_cont]'] = searchName
    if (filters.subscription_status) searchParams['subscription_status'] = filters.subscription_status
    if (filters.plan) searchParams['plan'] = filters.plan

    // Add sort parameter
    searchParams['q[s]'] = newSort

    router.get('/admin/organizations', searchParams, { preserveState: true })
  }

  const getSortIcon = (field: string) => {
    const params = new URLSearchParams(window.location.search)
    const currentSort = params.get('q[s]')

    if (currentSort === `${field} asc`) {
      return '↑'
    } else if (currentSort === `${field} desc`) {
      return '↓'
    }
    return '⇅'
  }

  const getSubscriptionStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge variant="success" className="text-xs px-1.5 py-0.5">{t('organizations.index.badges.active')}</Badge>
      case 'past_due':
        return <Badge variant="warning" className="text-xs px-1.5 py-0.5">{t('organizations.index.badges.pastDue')}</Badge>
      case 'canceled':
        return <Badge variant="destructive" className="text-xs px-1.5 py-0.5">{t('organizations.index.badges.canceled')}</Badge>
      case 'none':
        return <Badge variant="secondary" className="text-xs px-1.5 py-0.5">{t('organizations.index.badges.none')}</Badge>
      default:
        return <Badge variant="secondary" className="text-xs px-1.5 py-0.5">{status}</Badge>
    }
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount)
  }

  return (
    <div className="flex flex-1 flex-col">
        <div className="@container/main flex flex-1 flex-col gap-2">
          <div className="flex flex-col py-2">
            <div className="px-3">
              {/* Compact Filter Bar */}
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <span className="text-sm font-semibold whitespace-nowrap">
                  {t('organizations.index.titleWithCount', { count: pagy.count })}
                </span>
                <div className="w-px h-4 bg-border" />
                <Select value={filters.subscription_status || 'active'} onValueChange={handleSubscriptionStatusChange}>
                  <SelectTrigger className="h-7 text-xs w-28">
                    <SelectValue placeholder={t('organizations.index.table.status')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">{t('organizations.index.filters.status.active')}</SelectItem>
                    <SelectItem value="past_due">{t('organizations.index.filters.status.pastDue')}</SelectItem>
                    <SelectItem value="canceled">{t('organizations.index.filters.status.canceled')}</SelectItem>
                    <SelectItem value="none">{t('organizations.index.filters.status.none')}</SelectItem>
                    <SelectItem value="all">{t('organizations.index.filters.status.all')}</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={filters.plan || 'paid'} onValueChange={handlePlanChange}>
                  <SelectTrigger className="h-7 text-xs w-28">
                    <SelectValue placeholder={t('organizations.index.table.plan')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="paid">{t('organizations.index.filters.plan.paidOnly')}</SelectItem>
                    <SelectItem value="free">{t('organizations.index.filters.plan.free')}</SelectItem>
                    <SelectItem value="standard">{t('organizations.index.filters.plan.standard')}</SelectItem>
                    <SelectItem value="pro">{t('organizations.index.filters.plan.pro')}</SelectItem>
                    <SelectItem value="team">{t('organizations.index.filters.plan.team')}</SelectItem>
                    <SelectItem value="all">{t('organizations.index.filters.plan.all')}</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={filters.sort || 'default'} onValueChange={handleSortChange}>
                  <SelectTrigger className="h-7 text-xs w-36">
                    <SelectValue placeholder="Sort" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="default">{t('organizations.index.filters.sort.default')}</SelectItem>
                    <SelectItem value="revenue_desc">{t('organizations.index.filters.sort.revenueDesc')}</SelectItem>
                    <SelectItem value="revenue_asc">{t('organizations.index.filters.sort.revenueAsc')}</SelectItem>
                    <SelectItem value="credits_used_desc">{t('organizations.index.filters.sort.creditsUsedDesc')}</SelectItem>
                    <SelectItem value="last_activity_desc">{t('organizations.index.filters.sort.lastActivityDesc')}</SelectItem>
                  </SelectContent>
                </Select>
                <div className="w-px h-4 bg-border" />
                <Input
                  type="text"
                  placeholder={t('organizations.index.filters.namePlaceholder')}
                  className="h-7 text-xs w-36"
                  value={searchName}
                  onChange={(e) => setSearchName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                />
                <Button onClick={handleSearch} className="h-7 px-2 text-xs">
                  {t('organizations.index.buttons.search')}
                </Button>
                <Button onClick={handleReset} variant="ghost" className="h-7 px-2 text-xs">
                  {t('organizations.index.buttons.reset')}
                </Button>
              </div>

              {/* Organizations Table */}
              <div className="bg-card rounded-lg border border-border overflow-hidden">
                <div className="overflow-y-auto" style={{ maxHeight: 'calc(100vh - 160px)', minHeight: '400px' }}>
                  <table className="w-full">
                    <thead className="bg-muted sticky top-0 z-10">
                      <tr>
                        <th
                          className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground cursor-pointer hover:bg-muted/80 select-none"
                          onClick={() => handleSort('name')}
                        >
                          <div className="flex items-center gap-1">
                            {t('organizations.index.table.name')}
                            <span className="text-gray-400">{getSortIcon('name')}</span>
                          </div>
                        </th>
                        <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground">
                          {t('organizations.index.table.status')}
                        </th>
                        <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground">
                          {t('organizations.index.table.plan')}
                        </th>
                        <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground">
                          {t('organizations.index.table.revenue')}
                        </th>
                        <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground">
                          {t('organizations.index.table.credits')}
                        </th>
                        <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground">
                          {t('organizations.index.table.users')}
                        </th>
                        <th
                          className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground cursor-pointer hover:bg-muted/80 select-none"
                          onClick={() => handleSort('created_at')}
                        >
                          <div className="flex items-center gap-1">
                            {t('organizations.index.table.created')}
                            <span className="text-gray-400">{getSortIcon('created_at')}</span>
                          </div>
                        </th>
                        <th className="px-3 py-1.5 text-right text-xs font-medium text-muted-foreground">
                          {t('organizations.index.table.actions')}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-card divide-y divide-border">
                      {organizations.length > 0 ? (
                        organizations.map((org) => (
                          <tr key={org.id} className="hover:bg-muted transition-colors">
                            <td className="px-3 py-1.5 whitespace-nowrap">
                              <span className="text-sm font-medium text-foreground">{org.name}</span>
                            </td>
                            <td className="px-3 py-1.5 whitespace-nowrap">
                              {getSubscriptionStatusBadge(org.subscription_status)}
                            </td>
                            <td className="px-3 py-1.5 whitespace-nowrap">
                              <div className="flex items-center gap-1">
                                <Badge
                                  variant={org.current_plan === 'free' ? 'secondary' : 'default'}
                                  className="text-xs px-1.5 py-0.5"
                                >
                                  {org.plan_display_name}
                                </Badge>
                                {org.internal && (
                                  <Badge variant="outline" className="text-xs px-1.5 py-0.5 border-amber-500 text-amber-600">
                                    {t('organizations.index.badges.internal')}
                                  </Badge>
                                )}
                              </div>
                            </td>
                            <td className="px-3 py-1.5 whitespace-nowrap">
                              <span className="text-sm font-medium text-foreground">
                                {org.monthly_revenue > 0 ? formatCurrency(org.monthly_revenue) : '-'}
                              </span>
                            </td>
                            <td className="px-3 py-1.5 whitespace-nowrap">
                              <span className="text-sm text-foreground">
                                {org.credits_available.toFixed(0)}<span className="text-muted-foreground">/{org.credits_used.toFixed(0)}</span>
                              </span>
                            </td>
                            <td className="px-3 py-1.5 whitespace-nowrap">
                              <span className="text-sm text-foreground">{org.users_count}</span>
                            </td>
                            <td className="px-3 py-1.5 whitespace-nowrap">
                              <span className="text-sm text-foreground">
                                {new Date(org.created_at).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric'
                                })}
                              </span>
                            </td>
                            <td className="px-3 py-1.5 whitespace-nowrap text-right">
                              <Link
                                href={`/admin/organizations/${org.id}`}
                                className="text-sm text-blue-600 hover:text-blue-900"
                              >
                                {t('organizations.index.table.view')}
                              </Link>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={8} className="px-3 py-8 text-center text-sm text-muted-foreground">
                            {t('organizations.index.empty')}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Compact Pagination */}
                {pagy.pages > 1 && (
                  <div className="px-3 py-1.5 flex items-center justify-between border-t border-border bg-card text-xs">
                    <span className="text-muted-foreground">
                      {pagy.from}-{pagy.to} of {pagy.count}
                    </span>
                    <div className="flex gap-1">
                      {pagy.prev && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 px-2 text-xs"
                          onClick={() => router.get('/admin/organizations', { ...buildParams(), page: pagy.prev }, { preserveState: true })}
                        >
                          {t('organizations.index.pagination.previous')}
                        </Button>
                      )}
                      {pagy.next && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 px-2 text-xs"
                          onClick={() => router.get('/admin/organizations', { ...buildParams(), page: pagy.next }, { preserveState: true })}
                        >
                          {t('organizations.index.pagination.next')}
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
  )
}

Index.layout = (page: React.ReactNode) => <AdminLayout>{page}</AdminLayout>

export default Index
